import { COUPON_CODE_REGEX } from '@lib/models/aop'

// A QR's mcId is always exactly this many characters, in the first field.
export const MC_ID_LENGTH = 6

// '|' first: it can never be part of a coupon / mcId, so when present it is
// the only real separator ('FF99' inside a field is then just content).
const FIELD_SEPARATORS = ['|', 'FF99']

/** The field separator a scan uses, if any. */
function detectSeparator(code: string): string | undefined {
  return FIELD_SEPARATORS.find((sep) => code.includes(sep))
}

/** Extracts a QR's mcId: the first MC_ID_LENGTH characters. */
export function extractMcId(code: string): string {
  return code.slice(0, MC_ID_LENGTH)
}

/**
 * Splits a scanned coupon into its code and, when the scan is an identified
 * coupon (code + extra fields), the mcId carried in the 2nd field. The mcId
 * is taken as-is: it is NOT related to the coupon code, so it is never
 * derived from it. A bare coupon (or a manual entry) has no mcId.
 *
 * The coupon code has no fixed length, so it is whatever comes before the
 * first separator that leaves a valid coupon code (at least
 * COUPON_CODE_MIN_LENGTH chars) and is followed by a well-formed mcId
 * (exactly MC_ID_LENGTH chars, then the end of the scan or another
 * separator). Once a separator leaves a valid coupon the search stops there:
 * letting the coupon swallow a separator too would turn a malformed scan
 * into a bogus coupon + mcId. If the mcId is not well-formed, the coupon is
 * what precedes the first separator and there is no mcId.
 *
 * Known, accepted ambiguity: with the 'FF99' separator, a coupon whose first
 * characters spell 'FF99' after at least COUPON_CODE_MIN_LENGTH chars is
 * read as ending there. With '|' there is no such ambiguity.
 */
export function parseCouponScan(raw: string): { coupon: string; mcId?: string } {
  if (COUPON_CODE_REGEX.test(raw)) return { coupon: raw }
  const separator = detectSeparator(raw)
  if (!separator) return { coupon: raw }

  const first = raw.indexOf(separator)
  for (let i = first; i !== -1; i = raw.indexOf(separator, i + 1)) {
    const coupon = raw.slice(0, i)
    if (!COUPON_CODE_REGEX.test(coupon)) continue
    const field = raw.slice(i + separator.length)
    const mcId = extractMcId(field)
    const afterMcId = field.slice(MC_ID_LENGTH)
    // the 2nd field must be exactly an mcId: followed by the end of the scan or a real separator.
    // That rejects short fields (they would swallow part of the next separator) and longer ones
    if (mcId.length === MC_ID_LENGTH && (afterMcId === '' || afterMcId.startsWith(separator))) {
      return { coupon, mcId }
    }
    break
  }
  return { coupon: raw.slice(0, first) }
}

/**
 * Whether `code` is a QR (identification) code: MC_ID_LENGTH chars of mcId
 * followed by a field separator, and not an identified coupon.
 *
 * Coupons have no fixed length, so a QR is told apart structurally:
 *  - a scan that is entirely a valid coupon code is a bare coupon;
 *  - a scan with no separator is never a QR;
 *  - a QR starts with the 6-char mcId right before the 1st separator;
 *  - an identified coupon (code | mcId | name | phone) can only look like a
 *    QR (mcId | name | phone | token) when its code is itself 6 chars long.
 *    Then the 3rd field decides: a QR's is the (numeric) phone, a coupon's
 *    is the name.
 *
 * Anything that isn't recognized as a QR falls through to the coupon path,
 * where its own format check bails out safely — safer than defaulting an
 * unrecognized scan into a real login attempt.
 */
export function isQrCode(code: string): boolean {
  if (COUPON_CODE_REGEX.test(code)) return false
  const separator = detectSeparator(code)
  if (!separator || !code.slice(MC_ID_LENGTH).startsWith(separator)) return false
  if (parseCouponScan(code).mcId === undefined) return true
  const thirdField = code.split(separator)[2]
  return thirdField !== undefined && /^\d+$/.test(thirdField)
}

const QR_SEPARATOR_REGEX = /FF99|\|/

/** QR de autenticación con 4 parámetros (el último es el token IM) */
export const qrHasToken = (qr: string): boolean => {
  const parts = qr.split(QR_SEPARATOR_REGEX)
  // >= 4 because the token (base64url JWT) could contain 'FF99'
  return parts.length >= 4 && !!parts[3]
}
